import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { LockIcon } from 'lucide-react';
import { seriesEvents, velarisFeature } from '../data/series';
import { EventWordmark } from './EventWordmark';
import { RollLink } from './RollLink';
const ease = [0.22, 1, 0.36, 1] as const;
export function ExperientialSeries() {
  const reduceMotion = useReducedMotion();
  return <section id="series" aria-labelledby="series-heading" className="relative w-full overflow-hidden bg-ink text-paper" data-id="element-441" data-mp="6gp0sdi2wlfg">
      {/* Cinematic background */}
      <motion.img src={velarisFeature.image} alt={velarisFeature.alt} className="absolute inset-0 h-full w-full object-cover object-[70%_50%]" initial={{
      scale: 1.08
    }} whileInView={{
      scale: reduceMotion ? 1.08 : 1
    }} viewport={{
      once: true,
      amount: 0.3
    }} transition={{
      duration: 6,
      ease
    }} data-id="element-442" data-mp="b8glw8igtyy7" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(0,0,0,0.78),rgba(0,0,0,0.35)_55%,rgba(0,0,0,0.55)),linear-gradient(to_top,rgba(0,0,0,0.7),rgba(0,0,0,0)_45%)]" aria-hidden="true" data-id="element-443" data-mp="3omblh8a839qq" />
      <div className="film-grain pointer-events-none absolute inset-[-50%] opacity-[0.08] motion-reduce:hidden" aria-hidden="true" data-id="element-444" data-mp="17pvapdefcn8c" />

      <div className="relative z-[1] mx-auto grid w-full max-w-[1440px] grid-cols-1 items-end gap-14 px-6 pb-12 pt-20 md:gap-14 md:px-16 md:py-28 lg:min-h-[760px] lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)] lg:gap-14 lg:py-32" data-id="element-445" data-mp="1lnsa0hk9j7qp">
        {/* Venturist */}
        <motion.div initial={{
        opacity: 0,
        y: reduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.4
      }} transition={{
        duration: 1.2,
        ease
      }} data-id="element-446" data-mp="p16tb1f5h01b">
          {/* Hidden on phones, still read out by screen readers */}
          <p id="series-heading" className="sr-only text-[15px] font-light text-paper/80 md:not-sr-only" data-id="element-447" data-mp="a9ogtdli1nmp">
            Seldom spoken of — the Superluxury private series
          </p>
          <h2 className="md:mt-10 lg:mt-14" data-id="element-448" data-mp="25exj6lvxsfcf">
            <span className="sr-only" data-id="element-449" data-mp="25c1mt1zqlyq3">Venturist</span>
            <EventWordmark name="Venturist" className="text-[88px] md:text-[132px] lg:text-[160px]" data-id="element-450" data-mp="2ac77tzbi4mz7" />
          </h2>
          <p className="mt-1 text-[18px] font-light leading-[26px] md:mt-6 md:text-[24px] md:leading-[28px]" data-id="element-451" data-mp="2f5a2ybvxg4hi">{velarisFeature.line}</p>

          <div className="mt-4 md:mt-10" data-id="element-452" data-mp="2k4v4mu2heqxx">
            <RollLink href={velarisFeature.href} label="Discover Venturist" className="!pb-1 text-small font-bold uppercase tracking-[0.16em] md:!pb-2.5" data-id="element-453" data-mp="1mg38pp3ia122" />
          </div>
        </motion.div>

        {/* Other chapters — tall cinematic frames */}
        <div data-id="element-454" data-mp="20fb6cdk0t145">
          <p className="text-[13px] font-light text-paper/65" data-id="element-455" data-mp="20fuej2jvcb4z">Other chapters, by invitation</p>
          <ul className="mt-3 grid grid-cols-2 gap-3 md:mt-5 md:grid-cols-4 md:gap-4" data-id="element-456" data-mp="37ruubccekgl2">
            {seriesEvents.map((event, i) => <motion.li key={event.name} initial={{
            opacity: 0,
            y: reduceMotion ? 0 : 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.9,
            ease,
            delay: 0.2 + i * 0.08
          }} data-id="element-457" data-mp="3bwawp3mt55vj" data-mpi={i}>
                <a href={event.href} aria-label={`${event.name} — ${event.place}. Members only, by invitation`} className="group relative block aspect-[3/4] overflow-hidden md:aspect-[3/5] bg-paper/5 outline-none ring-paper/0 transition-[box-shadow] duration-300 focus-visible:ring-1 focus-visible:ring-paper" data-id="element-458" data-mp="mfhjylt0e55o">
                  <img src={event.image} alt={event.alt} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1600ms] ease-luxe group-hover:scale-[1.03]" data-id="element-459" data-mp="8ht09haoh427" />
                  <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(0,0,0,0.85),rgba(0,0,0,0.15)_55%,rgba(0,0,0,0.25))] transition-opacity duration-700 ease-luxe group-hover:opacity-80" aria-hidden="true" data-id="element-460" data-mp="1xvy4uunc1dzx" />
                  <div className="absolute inset-x-0 bottom-0 p-4 md:p-5" data-id="element-461" data-mp="lju80800jb42">
                    <h3 data-id="element-462" data-mp="qhvpwplpd4dy">
                      <EventWordmark name={event.name} className="text-[44px] md:text-[42px] xl:text-[50px]" data-id="element-463" data-mp="qhciw3xlc0ep" />
                    </h3>
                    <p className="mt-3 text-[13px] font-light leading-[18px] text-paper/80" data-id="element-464" data-mp="brwtosdpk314">{event.place}</p>
                    <LockIcon className="mt-4 h-4 w-4 text-paper/70 transition-[color,transform] duration-500 ease-luxe group-hover:-translate-y-0.5 group-hover:text-paper" strokeWidth={1.5} aria-hidden="true" data-id="element-465" data-mp="gm11teypmh3s" />
                  </div>
                </a>
              </motion.li>)}
          </ul>
        </div>
      </div>
    </section>;
}